import { useState, useRef, useCallback } from "react";
import ReactCrop from "react-image-crop";
import "react-image-crop/dist/ReactCrop.css";
import {
  Dialog,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  FormControlLabel,
  Switch,
  useMediaQuery,
  useTheme,
} from "@mui/material";

export default function CropImage({ src, onUpload, onCancel }) {
  const [crop, setCrop] = useState({ unit: "%", width: 80 });
  const [completedCrop, setCompletedCrop] = useState(null);
  const [maintainAspect, setMaintainAspect] = useState(false);
  const imgRef = useRef(null);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const onImgLoad = useCallback((e) => {
    imgRef.current = e.currentTarget;
  }, []);

  const getCroppedImgBlob = () => {
    return new Promise((resolve, reject) => {
      if (!completedCrop || !imgRef.current) {
        reject(new Error("Crop not completed"));
        return;
      }
      const image = imgRef.current;
      const canvas = document.createElement("canvas");
      const scaleX = image.naturalWidth / image.width;
      const scaleY = image.naturalHeight / image.height;

      canvas.width = completedCrop.width * scaleX;
      canvas.height = completedCrop.height * scaleY;

      const ctx = canvas.getContext("2d");

      ctx.drawImage(
        image,
        completedCrop.x * scaleX,
        completedCrop.y * scaleY,
        completedCrop.width * scaleX,
        completedCrop.height * scaleY,
        0,
        0,
        completedCrop.width * scaleX,
        completedCrop.height * scaleY
      );

      canvas.toBlob(
        (blob) => {
          if (!blob) {
            reject(new Error("Canvas is empty"));
            return;
          }
          resolve(blob);
        },
        "image/jpeg",
        0.95
      );
    });
  };

  const handleUpload = async () => {
    try {
      const blob = await getCroppedImgBlob();
      onUpload?.(blob);
    } catch (err) {
      console.error("Error cropping image:", err);
    }
  };

  const isValidCrop =
    completedCrop && completedCrop.width > 10 && completedCrop.height > 10;

  return (
    <Dialog
      open={true}
      onClose={onCancel}
      fullScreen={isMobile}
      maxWidth={isMobile ? "sm" : "md"}
      fullWidth
    >
      <DialogContent sx={{ p: isMobile ? 2 : 3 }}>
        <Box
          display="flex"
          justifyContent={isMobile ? "center" : "space-between"}
          alignItems={isMobile ? "flex-start" : "center"}
          flexDirection={isMobile ? "column" : "row"}
          gap={isMobile ? 1 : 0}
          mb={2}
        >
          <Typography variant="h6">Crop Your Image</Typography>
          <FormControlLabel
            control={
              <Switch
                checked={maintainAspect}
                onChange={(e) => {
                  setMaintainAspect(e.target.checked);
                  if (e.target.checked) {
                    setCrop((prev) => ({ ...prev, aspect: 4 / 3 }));
                  } else {
                    setCrop((prev) => {
                      const { aspect, ...rest } = prev;
                      return rest;
                    });
                  }
                }}
                size="small"
              />
            }
            label={<Typography variant="body2">Lock Aspect (4:3)</Typography>}
          />
        </Box>
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            bgcolor: "grey.100",
            borderRadius: 2,
            p: isMobile ? 1.5 : 2,
            minHeight: isMobile ? 240 : 400,
            border: "1px solid",
            borderColor: "grey.300",
          }}
        >
          <ReactCrop
            crop={crop}
            onChange={(c) => setCrop(c)}
            onComplete={(c) => setCompletedCrop(c)}
            aspect={maintainAspect ? 4 / 3 : undefined}
            style={{ maxWidth: "100%", maxHeight: isMobile ? 320 : 500 }}
          >
            <img
              src={src}
              onLoad={onImgLoad}
              alt="Crop preview"
              style={{
                width: "100%",
                maxHeight: isMobile ? 320 : 500,
                objectFit: "contain",
                borderRadius: 8,
              }}
            />
          </ReactCrop>
        </Box>
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ mt: 2, display: "block", textAlign: "center" }}
        >
          Drag the corners to resize • Drag the center to reposition
        </Typography>
      </DialogContent>
      <DialogActions
        sx={{
          p: isMobile ? 2 : 3,
          gap: isMobile ? 1 : 2,
          flexDirection: isMobile ? "column" : "row",
          alignItems: isMobile ? "stretch" : "center",
        }}
      >
        <Button onClick={onCancel} variant="outlined" fullWidth={isMobile}>
          Cancel
        </Button>
        <Button
          onClick={handleUpload}
          variant="contained"
          disabled={!isValidCrop}
          fullWidth={isMobile}
        >
          Crop & Upload
        </Button>
      </DialogActions>
    </Dialog>
  );
}